import type { SceneGroup } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { ErrorReporter } from 'js/src/libs/error-reporter';
import { awaitWithTimeout } from 'js/src/luminary/can-play-utils';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import { getVoiceDrivenAssetDurationAfterCutsApplied } from 'js/src/luminary/luminary-selectors';
import voicedrivenSceneGroupDurationFixerSchedule from 'js/src/luminary/voicedriven-scenegroup-duration-fixer-schedule';
import {
    getVoicedrivenSceneDuration,
    runVoicedrivenBatchRun,
    runVoicedrivenTranscriptBatchOperation,
    setVoicedrivenSceneDuration,
} from 'js/src/luminary/voicedriven-scenes';

/**
 * Opt-in bound for the playability wait, for callers that must not be wedged by a stalled media
 * load. Left unset the wait is unbounded, which is what the editor paths want: they would rather
 * wait for the real asset duration than fix against a rounded one and never re-run.
 */
export const DURATION_FIX_CAN_PLAY_TIMEOUT_MS = 10_000;

/**
 * Synchronous variant of {@link updateSceneGroupDurationToEqualVoiceDrivenAssetDuration}: mutates
 * the last scene's logical span so the scene group's total raw duration matches the voicedriven
 * asset's duration-after-cuts.
 *
 * MUST be called from inside a {@link runVoicedrivenBatchRun} — uses
 * {@link setVoicedrivenSceneDuration} so the end-of-batch reconcile adds the transition back via
 * `Scene.calculateDurationForLogicalSpan`, keeping snapshot bookkeeping consistent.
 *
 * Use this when you're already inside a voicedriven batch (e.g. combining this fix with an
 * `uncut()` call) and have already awaited the asset load. Otherwise prefer the async wrapper.
 */
export const synchronouslyUpdateSceneGroupDurationToEqualVoiceDrivenAssetDuration = (
    sceneGroup: SceneGroup,
) => {
    const voiceDrivenAssetDuration = getVoiceDrivenAssetDurationAfterCutsApplied(sceneGroup);
    if (voiceDrivenAssetDuration === 0) {
        // things are obviously not set up yet.
        return;
    }
    const lastScene = sceneGroup.scenes.at(-1);
    if (!lastScene) {
        throw new Error('No last scene found');
    }
    const durationDiff = voiceDrivenAssetDuration - sceneGroup.duration;
    if (durationDiff >= 0) {
        // Bump the last scene's logical span by the raw-sum delta. For scenes whose transition
        // isn't being capped by scaleFactor, delta(raw) == delta(logical) and the end-of-batch
        // reconcile produces a raw value that closes the gap exactly.
        setVoicedrivenSceneDuration(
            lastScene,
            getVoicedrivenSceneDuration(lastScene) + durationDiff,
        );
        return;
    }
    // The scene group is longer than the asset, so the excess lives past the end of the
    // voicedriven asset. Walk the scenes backwards: delete scenes the excess fully consumes
    // then trim the first scene that can absorb what remains.
    let excess = -durationDiff;
    for (let i = sceneGroup.scenes.length - 1; i >= 0 && excess > 0; i--) {
        const scene = sceneGroup.scenes[i];
        const sceneDuration = getVoicedrivenSceneDuration(scene);
        const remainingDuration = sceneDuration - excess;
        if (remainingDuration >= VideoData.MIN_SCENE_DURATION) {
            setVoicedrivenSceneDuration(scene, remainingDuration);
            return;
        }
        if (sceneGroup.scenes.length === 1) {
            // never delete the only scene; clamp it and accept the residual mismatch
            setVoicedrivenSceneDuration(
                scene,
                Math.max(remainingDuration, VideoData.MIN_SCENE_DURATION),
            );
            return;
        }
        sceneGroup.removeScene(scene.id);
        excess -= sceneDuration;
    }
    if (excess < 0) {
        // scenes must be at least 1 millisecond long. We might have deleted a scene to avoid
        // making it less than 1 millisecond long. In this case, we need to give the difference
        // back to the scene that is now last
        const newLastScene = sceneGroup.scenes.at(-1);
        if (newLastScene) {
            setVoicedrivenSceneDuration(
                newLastScene,
                getVoicedrivenSceneDuration(newLastScene) - excess,
            );
        }
    }
};

/**
 * The applied source data duration is usually a rounded off value
 * and we only get the 'real' duration of the asset after it has loaded.
 *
 * When this happens, we want to make sure that the scene duration matches
 * the loaded asset duration, so we add the diff to the last scene.
 *
 * If we don't do this, we end up with a scene group longer than the media
 * which results in the base scene design showing from the end time
 * of the last transcript word till the end of the scene group.
 *
 * The adjustment is routed through {@link runVoicedrivenBatchRun} so the end-of-batch reconcile
 * adds transitions back via `Scene.calculateDurationForLogicalSpan`. This keeps the snapshot
 * bookkeeping consistent with any other voicedriven batch that might be in flight.
 *
 * Callers that are already inside a voicedriven batch and want to combine this fix with other
 * synchronous work should call {@link synchronouslyUpdateSceneGroupDurationToEqualVoiceDrivenAssetDuration}
 * directly.
 */
const fixSceneGroupDurationAfterAssetLoad = async (
    sceneGroup: SceneGroup,
    waitForLuminary: boolean,
    applyDurationFix: () => void,
    canPlayTimeoutMs?: number,
): Promise<void> => {
    voicedrivenSceneGroupDurationFixerSchedule.increment(sceneGroup.id);
    try {
        if (waitForLuminary) {
            if (canPlayTimeoutMs === undefined) {
                await sceneGroup.root.onCanPlay();
            } else {
                // A stalled media load never settles onCanPlay. Callers that pass a bound would
                // rather fix against a rounded duration than hang, so swallow the expiry.
                try {
                    await awaitWithTimeout(
                        sceneGroup.root.onCanPlay(),
                        canPlayTimeoutMs,
                        'Scene group did not become playable before the timeout',
                    );
                } catch (error) {
                    ErrorReporter.capture(error, { level: 'warning' });
                }
            }
        }
        if (sceneGroup.destroyed) {
            return; // fine, the scene was destroyed before the asset loaded
        }

        applyDurationFix();
    } finally {
        voicedrivenSceneGroupDurationFixerSchedule.decrement(sceneGroup.id);
    }
};

/**
 * @deprecated Use {@link updateSceneGroupDurationToEqualVoiceDrivenAssetDurationOperation} instead.
 */
export const updateSceneGroupDurationToEqualVoiceDrivenAssetDuration = ({
    sceneGroup,
    waitForLuminary = true,
    canPlayTimeoutMs,
}: {
    sceneGroup: SceneGroup;
    waitForLuminary?: boolean;
    canPlayTimeoutMs?: number;
}): Promise<void> =>
    fixSceneGroupDurationAfterAssetLoad(
        sceneGroup,
        waitForLuminary,
        () => {
            runVoicedrivenBatchRun(sceneGroup.root, sceneGroup.id, () => {
                synchronouslyUpdateSceneGroupDurationToEqualVoiceDrivenAssetDuration(sceneGroup);
            });
        },
        canPlayTimeoutMs,
    );

/**
 * Operation form of {@link updateSceneGroupDurationToEqualVoiceDrivenAssetDuration}.
 *
 * Fixes scene group duration without messing up any custom-timed assets.
 */
export const updateSceneGroupDurationToEqualVoiceDrivenAssetDurationOperation =
    createLuminaryOperation(
        (
            { call },
            {
                sceneGroup,
                waitForLuminary = true,
                canPlayTimeoutMs,
            }: {
                sceneGroup: SceneGroup;
                waitForLuminary?: boolean;
                canPlayTimeoutMs?: number;
            },
        ): Promise<void> =>
            fixSceneGroupDurationAfterAssetLoad(
                sceneGroup,
                waitForLuminary,
                () => {
                    call(runVoicedrivenTranscriptBatchOperation, sceneGroup.id, () => {
                        synchronouslyUpdateSceneGroupDurationToEqualVoiceDrivenAssetDuration(
                            sceneGroup,
                        );
                    });
                },
                canPlayTimeoutMs,
            ),
    );
